// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { type AskPunttReview } from "@mg/schemas/src/christo/askPunttReview";
import { type ReactNode } from "react";

import { AskV2ReviewCompareBar } from "./AskV2ReviewCompareBar";
import {
  AskV2ReviewCompareToggle,
  useVersionCompareSeen,
} from "./AskV2ReviewCompareToggle";
import { type useAskV2ReviewVersionCompare } from "./useAskV2ReviewVersionCompare";
import { type ReviewVersionOption, VersionPicker } from "./VersionPicker";

type AskV2ReviewArtworkHeaderProps = {
  review: AskPunttReview;
  compare: ReturnType<typeof useAskV2ReviewVersionCompare>;
  versionOptions: readonly ReviewVersionOption[];
  onSwitchReview: (reviewId: string) => void;
  /** The chat's messages, whose version checks the Compare popover counts. */
  historyMessages: readonly Pick<
    AskPunttMessage,
    "comparisons" | "viewReviewId"
  >[];
  /**
   * The left column's pane switcher. Left out of the compare bar: the same
   * tabs stay under the chat field.
   */
  paneTabs: ReactNode;
  /** The viewer's rotate and zoom controls. */
  viewerControls: ReactNode;
};

/**
 * The row over the artwork column: the version picker, Compare and the
 * viewer controls, or while comparing, the one compare bar in their place.
 */
export function AskV2ReviewArtworkHeader({
  review,
  compare,
  versionOptions,
  onSwitchReview,
  historyMessages,
  paneTabs,
  viewerControls,
}: AskV2ReviewArtworkHeaderProps) {
  const { canCompare, isComparing, versionMark } = compare;
  useVersionCompareSeen(isComparing);
  return (
    <div
      // why: h-12 is PANE_HEADER_HEIGHT_CLASS, matching the left column's
      // Back header so both borders sit on one line; ask-v2 cannot
      // import the V1 module that exports it
      className="flex h-12 shrink-0 items-center gap-3 border-b border-puntt-neutral-gray-5 bg-base-white px-4"
      data-testid="ask-v2-review-artwork-header"
    >
      {isComparing ? (
        <AskV2ReviewCompareBar
          comparedReview={compare.comparedQuery.data?.review}
          comparedOptions={compare.compareOptions}
          comparedMark={versionMark(compare.compareReviewId)}
          onSelectCompared={compare.setCompareChoice}
          openReview={review}
          openOptions={versionOptions}
          openMark={versionMark(review.reviewId)}
          onSelectOpen={onSwitchReview}
          openIsEarlier={compare.openIsEarlier}
          syncPanes={compare.syncPanes}
          onSyncPanesChange={compare.setSyncPanes}
          viewerControls={viewerControls}
          onExit={() => compare.setComparing(false)}
        />
      ) : (
        <>
          <VersionPicker
            review={review}
            options={versionOptions}
            onSelect={onSwitchReview}
            mark={versionMark(review.reviewId)}
          />
          {canCompare ? (
            <AskV2ReviewCompareToggle
              review={review}
              compareReviewId={compare.compareReviewId}
              versionOptions={versionOptions}
              historyMessages={historyMessages}
              onCompare={() => compare.setComparing(true)}
            />
          ) : null}
          {paneTabs}
          {viewerControls}
        </>
      )}
    </div>
  );
}
